import React, { useState, useEffect } from 'react';
import { getCurrentUser, signOut, updatePassword, fetchUserAttributes } from 'aws-amplify/auth';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { User, Lock, Mail, Shield, Eye, EyeOff, LogOut, HelpCircle, FileText, Settings } from 'lucide-react';
import PasswordStrengthMeter from '../auth/PasswordStrengthMeter';
import { validatePassword, validatePasswordsMatch, getCognitoErrorMessage } from '../../utils/passwordValidation';
import { TopNav } from '../ui/top-nav';

interface UserInfo {
  email: string;
  userId: string;
  emailVerified?: boolean;
}

export const ProfilePage: React.FC = () => {
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadUserInfo();
  }, []);

  const loadUserInfo = async () => {
    try {
      // Fetch basic user info
      const user = await getCurrentUser();

      // Fetch detailed attributes
      const attributes = await fetchUserAttributes();

      setUserInfo({
        email: attributes.email || 'Unknown',
        userId: user.userId,
        emailVerified: attributes.email_verified === 'true',
      });
    } catch (error) {
      // console.error('Error loading user info:', error);
      toast.error('Failed to load user information');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate passwords match
    if (!validatePasswordsMatch(newPassword, confirmPassword)) {
      toast.error('New passwords do not match');
      return;
    }

    // Validate password strength
    const validation = validatePassword(newPassword);
    if (!validation.isValid) {
      toast.error(validation.errors[0]); // Show first error
      return;
    }

    setIsChangingPassword(true);
    try {
      await updatePassword({
        oldPassword: currentPassword,
        newPassword: newPassword,
      });

      toast.success('Password updated successfully');
      setShowPasswordForm(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowCurrentPassword(false);
      setShowNewPassword(false);
      setShowConfirmPassword(false);
    } catch (error: any) {
      // console.error('Password change error:', error);
      const errorMessage = getCognitoErrorMessage(error);
      toast.error(errorMessage);
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      // console.error('Error signing out:', error);
      toast.error('Failed to sign out');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <TopNav />
      <main className="overflow-auto p-6 space-y-6" style={{ height: 'calc(100vh - 4.75rem)' }}>
        {/* Header */}
        <div className="flex items-center gap-3">
          <User className="h-5 w-5 text-primary" />
          <div>
            <h1 className="text-base font-semibold text-foreground">Profile Settings</h1>
            <p className="text-sm text-muted-foreground">
              Manage your account details and security preferences
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

          {/* Main Content - Left Column */}
          <div className="lg:col-span-2 space-y-8">

            {/* User Information Card */}
            <Card className="border-primary/20 shadow-md">
              <CardHeader className="bg-muted/30 border-b border-border/50 pb-4">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <User className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-xl">Account Information</CardTitle>
                    <CardDescription>Your personal account details</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center">
                      <Mail className="h-3 w-3 mr-1.5" />
                      Email Address
                    </Label>
                    <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg border border-border/50">
                      <span className="text-sm font-medium">{userInfo?.email}</span>
                      {userInfo?.emailVerified && (
                        <span className="text-[10px] bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 px-2 py-0.5 rounded-full font-medium">
                          Verified
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center">
                      <Shield className="h-3 w-3 mr-1.5" />
                      User ID
                    </Label>
                    <div className="p-3 bg-muted/30 rounded-lg border border-border/50">
                      <code className="text-xs font-mono text-muted-foreground break-all">
                        {userInfo?.userId}
                      </code>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Security Settings Card */}
            <Card>
              <CardHeader className="bg-muted/30 border-b border-border/50 pb-4">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Lock className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-xl">Security Settings</CardTitle>
                    <CardDescription>Manage your password and authentication</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                {!showPasswordForm ? (
                  <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg border border-border/50">
                    <div>
                      <h4 className="font-medium text-sm">Password</h4>
                      <p className="text-xs text-muted-foreground mt-1">
                        Change your account password
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => setShowPasswordForm(true)}
                    >
                      Change Password
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handlePasswordChange} className="space-y-5 bg-muted/10 p-6 rounded-xl border border-border/50">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium">Change Password</h4>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setShowPasswordForm(false);
                          setCurrentPassword('');
                          setNewPassword('');
                          setConfirmPassword('');
                        }}
                        className="h-8 px-2"
                      >
                        Cancel
                      </Button>
                    </div>

                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="current-password">Current Password</Label>
                        <div className="relative">
                          <Input
                            id="current-password"
                            type={showCurrentPassword ? "text" : "password"}
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            required
                            disabled={isChangingPassword}
                            className="pr-10"
                          />
                          <button
                            type="button"
                            onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            disabled={isChangingPassword}
                          >
                            {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="new-password">New Password</Label>
                        <div className="relative">
                          <Input
                            id="new-password"
                            type={showNewPassword ? "text" : "password"}
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            required
                            disabled={isChangingPassword}
                            className="pr-10"
                          />
                          <button
                            type="button"
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            disabled={isChangingPassword}
                          >
                            {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                        <PasswordStrengthMeter password={newPassword} showRequirements={true} />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="confirm-new-password">Confirm New Password</Label>
                        <div className="relative">
                          <Input
                            id="confirm-new-password"
                            type={showConfirmPassword ? "text" : "password"}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                            disabled={isChangingPassword}
                            className="pr-10"
                          />
                          <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            disabled={isChangingPassword}
                          >
                            {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                        {confirmPassword && (
                          <p className={`text-xs mt-1 flex items-center ${validatePasswordsMatch(newPassword, confirmPassword)
                              ? 'text-green-600 dark:text-green-400'
                              : 'text-destructive'
                            }`}>
                            {validatePasswordsMatch(newPassword, confirmPassword)
                              ? '✓ Passwords match'
                              : '✗ Passwords do not match'}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="pt-2">
                      <Button
                        type="submit"
                        disabled={isChangingPassword}
                        className="w-full sm:w-auto"
                      >
                        {isChangingPassword ? 'Updating Password...' : 'Update Password'}
                      </Button>
                    </div>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar - Right Column */}
          <div className="space-y-6">

            {/* Account Actions */}
            <Card className="border-destructive/20">
              <CardHeader>
                <CardTitle className="text-lg flex items-center">
                  <Settings className="h-5 w-5 mr-2" />
                  Actions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="p-4 bg-muted/30 rounded-lg border border-border/50">
                    <h4 className="font-medium text-sm mb-1">Sign Out</h4>
                    <p className="text-xs text-muted-foreground mb-3">
                      Securely sign out of your account on this device
                    </p>
                    <Button
                      variant="outline"
                      onClick={handleSignOut}
                      className="w-full hover:bg-destructive/10 hover:text-destructive hover:border-destructive/30"
                    >
                      <LogOut className="h-4 w-4 mr-2" />
                      Sign Out
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Help & Support */}
            <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 border-blue-200 dark:border-blue-800/50">
              <CardHeader>
                <CardTitle className="text-lg flex items-center">
                  <HelpCircle className="h-5 w-5 text-blue-600 mr-2" />
                  Support
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li>
                    <a href="#" className="flex items-center p-2 rounded-lg hover:bg-background/50 transition-colors group">
                      <div className="bg-blue-100 dark:bg-blue-900/50 p-1.5 rounded mr-3">
                        <FileText className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <span className="text-sm font-medium block group-hover:text-blue-600 transition-colors">Documentation</span>
                        <span className="text-xs text-muted-foreground">Read the guides</span>
                      </div>
                    </a>
                  </li>
                  <li>
                    <a href="#" className="flex items-center p-2 rounded-lg hover:bg-background/50 transition-colors group">
                      <div className="bg-blue-100 dark:bg-blue-900/50 p-1.5 rounded mr-3">
                        <HelpCircle className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <span className="text-sm font-medium block group-hover:text-blue-600 transition-colors">Contact Support</span>
                        <span className="text-xs text-muted-foreground">Get help with issues</span>
                      </div>
                    </a>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};